import { PRODUCT_PRICE_VARIANT_MSG } from '../../../constants';
import { captureSentryError } from '~/exceptions/sentry';
import { useCallback, useRef, useState } from 'react';
import { useToast } from '~/hooks/useToast';

export interface IUsePriceProductSyncParams {
  /** Re-reads the products from Shopify. Must reject on failure, or a broken sync reads as a good one. */
  onRefresh: () => Promise<void>;
}

export interface IUsePriceProductSyncResult {
  isSyncingControlPrices: boolean;
  handleRefreshControlPrices: () => Promise<void>;
}

/**
 * The Control header's sync action. A merchant edits prices in Shopify admin and comes back here, so
 * the outcome has to be reported: without a toast, a sync that changed nothing and one that failed
 * look identical.
 */
export const usePriceProductSync = ({
  onRefresh,
}: IUsePriceProductSyncParams): IUsePriceProductSyncResult => {
  const { shopifyToast } = useToast();
  const [isSyncingControlPrices, setIsSyncingControlPrices] = useState(false);
  const isSyncingRef = useRef(false);

  const handleRefreshControlPrices = useCallback(async () => {
    // A ref, not the state flag: two clicks in the same tick both read the pre-update state and would
    // each fire a full round of product reads.
    if (isSyncingRef.current) return;
    isSyncingRef.current = true;
    setIsSyncingControlPrices(true);

    try {
      await onRefresh();
      shopifyToast(PRODUCT_PRICE_VARIANT_MSG.SYNC.SUCCESS);
    } catch (error) {
      captureSentryError(error, {
        source: 'usePriceProductSync.handleRefreshControlPrices',
        tags: { experiment: 'Experiment' },
      });
      shopifyToast(PRODUCT_PRICE_VARIANT_MSG.SYNC.FAILED, { isError: true });
    } finally {
      isSyncingRef.current = false;
      setIsSyncingControlPrices(false);
    }
  }, [onRefresh, shopifyToast]);

  return { isSyncingControlPrices, handleRefreshControlPrices };
};
